import React from "react";
import styled from "styled-components";
import { componentTypes } from "./componentTypes";

/**
 * Renders one "card" – either in the tool menu or the dropped area.
 */
const ComponentElement = ({
    component,
    provided,
    snapshot,
    onClick,
    isSelected,
    factor,
    onDuplicate,
    index
  }) => {
    const { type } = component;
    
    // If type is multiSelect or date, use dropdown for display purposes
    const displayType = ['multiSelect', 'date'].includes(type) ? 'dropdown' : type;
  
    // If the user has set 'label' in the SettingsDrawer, we show that;
    // Otherwise, fallback to componentTypes[displayType].name (e.g. "Likert", "Dropdown").
    const displayText =
      component.properties.question || componentTypes[displayType].name;
  
    const handleDuplicateClick = (e) => {
      e.stopPropagation(); // Prevent triggering the parent onClick
      onDuplicate(component);
    };
    
    const handleClick = (e) => {
      // Prevent event bubbling
      e.stopPropagation();
      // Log component selection to help with debugging
      console.log("ComponentElement clicked:", component.uniqueId, "type:", component.type);
      // Call the parent's onClick handler
      onClick(component);
    };

    // Determine if this is an end screen component
    const isEndScreen = component.type === 'end_screen';

    return (
      <Container
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        onClick={handleClick}
        isSelected={isSelected}
        isEndScreen={isEndScreen}
        data-id={component.uniqueId}
      >
        <QuestionNumber>{index + 1}.</QuestionNumber>
        <Icon>{componentTypes[displayType].icon()}</Icon>
  
        <Text>{displayText} {factor && (<Factor>{factor}</Factor>)}</Text>
  
        <ActionContainer>
          {onDuplicate && (
            <DuplicateButton onClick={handleDuplicateClick} title="Duplicate question">
              <DuplicateSVG />
            </DuplicateButton>
          )}
          <Icon2 className="drag-handle">
            <DragSVG />
          </Icon2>
        </ActionContainer>
      </Container>
    );
  };
  
  export default ComponentElement;

/* --- STYLES --- */

const Container = styled.div`
  font-family: "Raleway", sans-serif;
  padding: 10px;
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  cursor: pointer;
  position: relative;
  border: 1px solid ${({ isSelected }) => (isSelected ? "blue" : "#ddd")};
  background-color: ${({ isEndScreen }) => (isEndScreen ? "#f7fbff" : "#fff")};
  user-select: none;
  border-radius: 5px; /* New addition */
  width:600px;

  &:hover {
    background-color: ${({ isEndScreen }) => (isEndScreen ? "#edf5ff" : "#f9f9f9")};
    .drag-handle {
      opacity: 1;
    }
  }
`;

const Icon = styled.div`
  height: 18px;
  margin-right: 10px;
`;

const Text = styled.div`
  flex: 1;
  display:flex;
  align-items:center;
  justify-content:space-between;
  width:90%;
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DuplicateButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  height: 18px;
  width: 18px;
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: #666D79;

  ${Container}:hover & {
    opacity: 1;
  }

  &:hover {
    color: #4a90e2;
  }
`;

const Icon2 = styled.div`
  height: 18px;
  opacity: 0;
  transition: opacity 0.2s ease;
  color:#666D79;
`;

const Factor = styled.div`
    font-size: 12px;
    margin-left: 10px;
    color: #2D70E2;
    padding: 4px 8px;
    border-radius: 16px;
    font-weight:600;
    background-color: #EBF1FD;
`;

const QuestionNumber = styled.div`
   font-size:12px;
   position:absolute;
   left:-20px;
`

const DuplicateSVG = () => (
  <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 8V16M8 8H16H8ZM8 16H16H8ZM8 8V16V8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7 4H4V20H16V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <rect x="8" y="4" width="12" height="12" rx="1" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const DragSVG = ()=>(
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
 <path d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
 <path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
 <path d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
 <path d="M19 6C19.5523 6 20 5.55228 20 5C20 4.44772 19.5523 4 19 4C18.4477 4 18 4.44772 18 5C18 5.55228 18.4477 6 19 6Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
 <path d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
 <path d="M19 20C19.5523 20 20 19.5523 20 19C20 18.4477 19.5523 18 19 18C18.4477 18 18 18.4477 18 19C18 19.5523 18.4477 20 19 20Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
 
 </svg>
)